import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { listMyGroups } from "../api/groups";
import { deliveredReceiptsE2EE } from "../api/messagesE2EE";
import {
  streamWebMessageEvents,
  type WebMessageRealtimeEvent,
} from "../api/messageEventsWeb";

type Props = {
  currentUserId: string;
};

type GroupLike = {
  id?: string | number;
  groupId?: string | number;
  group_id?: string | number;
  publicId?: string;
  public_id?: string;
  conversationId?: string;
  conversation_id?: string;
};

function groupConversationId(group: GroupLike): string {
  const direct = String(
    group?.conversationId ||
    group?.conversation_id ||
    "",
  ).trim();

  if (direct) return direct;

  const groupId = String(
    group?.id ||
    group?.groupId ||
    group?.group_id ||
    group?.publicId ||
    group?.public_id ||
    "",
  ).trim();

  return groupId ? `group:${groupId}:group` : "";
}

function eventSenderId(
  event: WebMessageRealtimeEvent,
): string {
  return String(
    event.senderUserId ||
    event.sender_user_id ||
    event.fromUserId ||
    event.from_user_id ||
    "",
  ).trim();
}

function eventMessageIds(
  event: WebMessageRealtimeEvent,
): Array<string | number> {
  return [
    ...(Array.isArray(event.messageIds)
      ? event.messageIds
      : []),
    ...(Array.isArray(event.message_ids)
      ? event.message_ids
      : []),
    event.messageId,
    event.message_id,
  ].filter(
    (value): value is string | number =>
      value !== null &&
      value !== undefined &&
      String(value).trim().length > 0,
  );
}

function eventClientMessageIds(
  event: WebMessageRealtimeEvent,
): string[] {
  return Array.from(
    new Set(
      [
        ...(Array.isArray(event.clientMessageIds)
          ? event.clientMessageIds
          : []),
        ...(Array.isArray(event.client_message_ids)
          ? event.client_message_ids
          : []),
        event.clientMessageId,
        event.client_message_id,
      ]
        .filter(
          (value): value is string =>
            typeof value === "string" &&
            value.trim().length > 0,
        )
        .map((value) => value.trim()),
    ),
  );
}

export default function GlobalMessageDelivery({
  currentUserId,
}: Props) {
  const location = useLocation();

  const [conversationIds, setConversationIds] =
    useState<string[]>([]);

  const onMessagesRoute = useMemo(() => {
    const pathname = String(
      location.pathname || "",
    ).replace(/\/+$/, "");

    return (
      pathname === "/messages" ||
      pathname.endsWith("/messages")
    );
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;

    if (!currentUserId) {
      setConversationIds([]);
      return;
    }

    void listMyGroups()
      .then((response: any) => {
        if (cancelled) return;

        const rows = Array.isArray(response)
          ? response
          : Array.isArray(response?.items)
            ? response.items
            : Array.isArray(response?.groups)
              ? response.groups
              : [];

        const ids = Array.from(
          new Set(
            rows
              .map((group: GroupLike) =>
                groupConversationId(group),
              )
              .filter(Boolean),
          ),
        );

        setConversationIds(
            ids.filter((id): id is string => typeof id === "string" && id.length > 0),
          );
      })
      .catch((error) => {
        console.warn(
          "[GlobalMessageDelivery] could not load groups",
          error,
        );
      });

    return () => {
      cancelled = true;
    };
  }, [currentUserId]);

  useEffect(() => {
    if (!currentUserId) return;

    // Messages.tsx owns the active chat stream and visible-read flow.
    if (onMessagesRoute) return;

    if (!conversationIds.length) return;

    const controllers = new Map<
      string,
      AbortController
    >();

    const reconnectTimers = new Map<
      string,
      number
    >();

    let cancelled = false;

    function clearReconnect(
      conversationId: string,
    ) {
      const timer = reconnectTimers.get(
        conversationId,
      );

      if (timer !== undefined) {
        window.clearTimeout(timer);
        reconnectTimers.delete(conversationId);
      }
    }

    function startConversationStream(
      conversationId: string,
      attempt = 0,
    ) {
      if (cancelled || onMessagesRoute) return;

      clearReconnect(conversationId);

      const previous =
        controllers.get(conversationId);

      if (previous && !previous.signal.aborted) {
        previous.abort();
      }

      const controller = new AbortController();

      controllers.set(
        conversationId,
        controller,
      );

      void streamWebMessageEvents(
        conversationId,
        controller.signal,
        async (event) => {
          const type = String(
            event?.type || "",
          ).toLowerCase();

          if (type !== "message:new") return;

          const senderId = eventSenderId(event);

          if (
            senderId &&
            senderId === String(currentUserId)
          ) {
            return;
          }

          const eventConversationId = String(
            event.conversationId ||
            event.conversation_id ||
            conversationId,
          ).trim();

          const messageIds =
            eventMessageIds(event);

          const clientMessageIds =
            eventClientMessageIds(event);

          if (
            !messageIds.length &&
            !clientMessageIds.length
          ) {
            console.warn(
              "[GlobalMessageDelivery] message:new had no identifiers",
              {
                conversationId:
                  eventConversationId,
              },
            );
            return;
          }

          try {
            await deliveredReceiptsE2EE(
              eventConversationId,
              {
                messageIds,
                clientMessageIds,
              },
              {
                realtime: true,
                source:
                  "web-global-sse-delivered",
              },
            );

            console.log(
              "[GlobalMessageDelivery] acknowledged delivered",
              {
                conversationId:
                  eventConversationId,
                messageIds,
                clientMessageIds,
              },
            );
          } catch (error) {
            console.warn(
              "[GlobalMessageDelivery] delivered acknowledgement failed",
              {
                conversationId:
                  eventConversationId,
                messageIds,
                clientMessageIds,
                error,
              },
            );
          }
        },
      )
        .then(() => {
          if (
            cancelled ||
            controller.signal.aborted
          ) {
            return;
          }

          const delay = Math.min(
            30_000,
            1_000 * Math.pow(2, attempt),
          );

          const timer = window.setTimeout(
            () =>
              startConversationStream(
                conversationId,
                attempt + 1,
              ),
            delay,
          );

          reconnectTimers.set(
            conversationId,
            timer,
          );
        })
        .catch((error) => {
          if (
            cancelled ||
            controller.signal.aborted
          ) {
            return;
          }

          console.warn(
            "[GlobalMessageDelivery] SSE disconnected",
            {
              conversationId,
              error,
            },
          );

          const delay = Math.min(
            30_000,
            1_000 * Math.pow(2, attempt),
          );

          const timer = window.setTimeout(
            () =>
              startConversationStream(
                conversationId,
                attempt + 1,
              ),
            delay,
          );

          reconnectTimers.set(
            conversationId,
            timer,
          );
        });
    }

    for (const conversationId of conversationIds) {
      startConversationStream(conversationId);
    }

    return () => {
      cancelled = true;

      for (const timer of reconnectTimers.values()) {
        window.clearTimeout(timer);
      }

      reconnectTimers.clear();

      for (const controller of controllers.values()) {
        controller.abort();
      }

      controllers.clear();
    };
  }, [
    conversationIds,
    currentUserId,
    onMessagesRoute,
  ]);

  return null;
}
